/**
 * Centralized logging utility for frontend applications.
 *
 * Provides structured logging with context, supports different log levels,
 * and can be extended to integrate with error tracking services (Sentry, etc.)
 */
const LOG_LEVELS = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
};
class Logger {
    config;
    constructor(config = {}) {
        const isProduction = typeof process !== 'undefined'
            ? process.env.NODE_ENV === 'production'
            : typeof window !== 'undefined' && window.location?.hostname !== 'localhost';
        this.config = {
            minLevel: config.minLevel ?? (isProduction ? 'info' : 'debug'),
            includeTimestamp: config.includeTimestamp ?? true,
            appName: config.appName ?? 'app',
        };
    }
    shouldLog(level) {
        return LOG_LEVELS[level] >= LOG_LEVELS[this.config.minLevel];
    }
    formatMessage(level, message, context) {
        const parts = [];
        if (this.config.includeTimestamp) {
            parts.push(`[${new Date().toISOString()}]`);
        }
        parts.push(`[${this.config.appName}]`);
        parts.push(`[${level.toUpperCase()}]`);
        if (context?.operation) {
            parts.push(`[${context.operation}]`);
        }
        parts.push(message);
        return parts.join(' ');
    }
    formatContext(context) {
        if (!context)
            return undefined;
        // Remove operation from context since it's already in the message
        const { operation, ...rest } = context;
        return Object.keys(rest).length > 0 ? rest : undefined;
    }
    debug(message, context) {
        if (!this.shouldLog('debug'))
            return;
        const formattedContext = this.formatContext(context);
        if (formattedContext) {
            console.debug(this.formatMessage('debug', message, context), formattedContext);
        }
        else {
            console.debug(this.formatMessage('debug', message, context));
        }
    }
    info(message, context) {
        if (!this.shouldLog('info'))
            return;
        const formattedContext = this.formatContext(context);
        if (formattedContext) {
            console.info(this.formatMessage('info', message, context), formattedContext);
        }
        else {
            console.info(this.formatMessage('info', message, context));
        }
    }
    warn(message, context) {
        if (!this.shouldLog('warn'))
            return;
        const formattedContext = this.formatContext(context);
        if (formattedContext) {
            console.warn(this.formatMessage('warn', message, context), formattedContext);
        }
        else {
            console.warn(this.formatMessage('warn', message, context));
        }
    }
    error(message, error, context) {
        if (!this.shouldLog('error'))
            return;
        const formattedContext = this.formatContext(context);
        const errorInfo = this.extractErrorInfo(error);
        if (formattedContext || errorInfo) {
            console.error(this.formatMessage('error', message, context), {
                ...formattedContext,
                ...errorInfo,
            });
        }
        else {
            console.error(this.formatMessage('error', message, context));
        }
        // Browser errors remain console-only while remote reporting is disabled.
    }
    extractErrorInfo(error) {
        if (!error)
            return undefined;
        if (error instanceof Error) {
            return {
                errorName: error.name,
                errorMessage: error.message,
                errorStack: error.stack,
            };
        }
        if (typeof error === 'object') {
            return { error };
        }
        return { error: String(error) };
    }
    /**
     * Create a child logger with additional context
     */
    child(context) {
        return new ChildLogger(this, context);
    }
}
class ChildLogger {
    parent;
    context;
    constructor(parent, context) {
        this.parent = parent;
        this.context = context;
    }
    mergeContext(additionalContext) {
        return { ...this.context, ...additionalContext };
    }
    debug(message, context) {
        this.parent.debug(message, this.mergeContext(context));
    }
    info(message, context) {
        this.parent.info(message, this.mergeContext(context));
    }
    warn(message, context) {
        this.parent.warn(message, this.mergeContext(context));
    }
    error(message, error, context) {
        this.parent.error(message, error, this.mergeContext(context));
    }
}
/**
 * Create a logger instance for a specific app
 */
export function createLogger(appName, config) {
    return new Logger({ ...config, appName });
}
/**
 * Default logger instance
 */
export const logger = new Logger();
//# sourceMappingURL=logger.js.map